<script setup lang="ts">
import type { Tag } from '~/ui-blocks/Formalization/Formalization.types'
import type { Project } from '~/utils/project/Project'
import { SolOnboarding, useToast } from '@solfacil/girassol'
import IconAccountBalanceWallet from '~icons/material-symbols/account-balance-wallet-outline'
import IconArrowRightAlt from '~icons/material-symbols/arrow-right-alt'
import IconAutoPause from '~icons/material-symbols/autopause'
import IconCalendarMonth from '~icons/material-symbols/calendar-month-outline'
import IconCheck from '~icons/material-symbols/check-small'
import IconLink from '~icons/material-symbols/info-outline'
import IconOpenInNew from '~icons/material-symbols/open-in-new'
import IconPaid from '~icons/material-symbols/paid-outline'
import IconAddPerson from '~icons/material-symbols/person-add-outline'
import IconSolarPower from '~icons/material-symbols/solar-power-outline'
import IconWarning from '~icons/material-symbols/warning-outline'
import Loading from '~/components/loading/index.vue'
import ComboConecteModal from '~/components/simulation/ComboConecteModal.vue'
import { useTrackSimulationResultViewed } from '~/composables/analytics/useTrackSimulationResultViewed'
import { useMoney } from '~/composables/money'
import { useWhatsApp } from '~/composables/whatsapp'
import { FEATURE_FLAGS } from '~/constants'
import SimulatorService from '~/services/simulator/Simulator'
import useAuth from '~/store-v2/auth/useAuth'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import usePartner from '~/store-v2/partners/usePartner'
import IncludeGuarantor from '~/ui-blocks/Simulation/IncludeGuarantor.vue'
import { getErrorMessage } from '~/utils/errors'
import { conventionalNameFormat } from '~/utils/formatter/name'
import { PROJECT_DECISION_REASON } from '~/utils/project/Project'
import { isKnownSystemType, isOnlyBattery } from '~/utils/project/systemType'

const { formatMoney, formatOnlyMoneyValue } = useMoney()
const isEnabledToBNPL = ref(false)
const isEnabledToBNPLInterest = ref(false)
const { openWhatsApp } = useWhatsApp()

// Feature flags
const { data: bnplFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.BNPL, false)
const { data: bnplInterestFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.BNPL_INTEREST, false)
const { data: subscriptionEnergyFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.SUBSCRIPTION_ENERGY, false)
const { data: comboConnectEnabledForIntegrator } = useFeatureFlag<boolean>(FEATURE_FLAGS.COMBO_CONNECT_ENABLED_FOR_INTEGRATOR, false, true)

type ClientStatus = 'approved' | 'pending' | 'reproved' | 'pending_guarantor' | 'necessary_install_pending_project'
type ClientStatusBNPL = 'approved' | 'reproved'

const {
  createErrorToast,
} = useToast()

const { t } = useI18n()

const router = useRouter()
const route = useRoute<any>()
const id = ref(route.params.id as string)
const { trackSimulationResultViewed } = useTrackSimulationResultViewed({ projectId: id })

const {
  data: financing,
  error,
  isError,
  isLoading,
} = useFinancing(id)
const errorMessage = computed(() => getErrorMessage(error.value))

const { data: auth } = useAuth()
const partnerId = computed(() => auth.value?.person?.partner?.id?.toString() || '')
const {
  data: partner,
  isLoading: isLoadingPartner,
} = usePartner(partnerId)
const userIsOnBlockedList = computed(() => {
  return partner.value?.blocklist?.is_blocked || false
})

const attentionCheckbox = ref(false)

const clientApproved = ref(true)
const toggleSimulationUpdateModal = ref(false)

function closeUpdateSimulationModal() {
  toggleSimulationUpdateModal.value = false
}

const simulatorService = new SimulatorService()

const projectResponse = ref<Project | null>(null)
const isOnlyBatteryProject = computed(() => isOnlyBattery(projectResponse.value?.system_type))
// Unknown system types (new backend value) hide the "Tipo sistema" line instead of showing a raw i18n key.
const isKnownProjectSystemType = computed(() => isKnownSystemType(projectResponse.value?.system_type))
const noResultMessage = ref(false)
const projectInactiveAndNotComplete = ref(false)
const downPayment = ref(0)
const projectValue = ref(0)
const projectName = ref('')
const documentIsCpf = ref(true)

const clientStatus = ref<ClientStatus>('approved')
const clientStatusBNPL = ref<ClientStatusBNPL>('approved')

const toggleAttentionModal = ref(false)
const toggleInterestModal = ref(false)
const toggleComboConecteModal = ref(false)
const comboConecteModalInitialTab = ref<'benefits' | 'inverters'>('benefits')

const analysisStatusSimulation = {
  approved: 1,
  pending: 2,
  pending_guarantor: 3,
  reproved_credit_policy: 4,
  reproved_internal_political_disagreement: 5,
  reproved_no_agreement: 6,
  reproved_credit_analysis_two: 8,
  necessary_install_pending_project: 10,
}

const analysisStatusBNPL = {
  approved_without_co_obligation: 2,
  reproved_try_again_30_days: 4,
  reproved_block_list: 5,
  reproved_block_integrator: 6,
  approved_with_co_obligation: 10,
}

const analyzingSimulation = ref(false)

const loadingMessages = t('loading.messages').split(', ')
const isLoadingGuarantor = ref(false)

function setFalseLoadingGuarantor() {
  isLoadingGuarantor.value = false
}

function setBaseProjectData(project: Project) {
  projectResponse.value = project

  documentIsCpf.value = project.document.length === 11
  downPayment.value = project.down_payment
  projectValue.value = project.project_value
  projectName.value = project.project_name
}

function isPendingAnalysis(project: Project): boolean {
  return project.credit_analysis_status === 'pending'
}

function isProjectInactive(project: Project): boolean {
  return project.project_status === 'INACTIVE'
}

function setClientStatus(project: Project) {
  const decision = project.risk_info.decision

  const statusMap: Record<string, string> = {
    [analysisStatusSimulation.approved]: 'approved',
    [analysisStatusSimulation.pending]: 'pending',
    [analysisStatusSimulation.pending_guarantor]: 'pending_guarantor',
    [analysisStatusSimulation.necessary_install_pending_project]: 'necessary_install_pending_project',
  }

  if (statusMap[decision]) {
    if (decision === analysisStatusSimulation.pending_guarantor && documentIsCpf.value) {
      clientStatus.value = statusMap[decision] as ClientStatus
    }
    else if (decision !== analysisStatusSimulation.pending_guarantor) {
      clientStatus.value = statusMap[decision] as ClientStatus
    }
  }

  const deniedStatuses = [
    analysisStatusSimulation.necessary_install_pending_project,
  ]

  if (
    decision > analysisStatusSimulation.pending_guarantor
    && !deniedStatuses.includes(decision)
  ) {
    clientStatus.value = 'reproved'
    clientApproved.value = false
    return
  }

  if (project.risk_class === 'Z') {
    clientStatus.value = 'reproved'
    clientApproved.value = false
  }
}

function setBNPLStatus(bnpl?: Project['bnpl']) {
  if (!bnpl) {
    clientStatusBNPL.value = 'reproved'
    return
  }

  const bnplStatusMap: Record<number, string> = {
    [analysisStatusBNPL.approved_without_co_obligation]: 'approved',
    [analysisStatusBNPL.approved_with_co_obligation]: 'approved',
    [analysisStatusBNPL.reproved_try_again_30_days]: 'reproved',
    [analysisStatusBNPL.reproved_block_list]: 'reproved',
    [analysisStatusBNPL.reproved_block_integrator]: 'reproved',
  }

  const status = bnplStatusMap[bnpl.decision_pre_analysis ?? 'approved']
  if (status) {
    clientStatusBNPL.value = status
  }
}

async function mountResponse() {
  isLoadingGuarantor.value = true
  const projectId = route.params.id

  try {
    const response = await simulatorService.get_project(projectId)
    const project: Project | undefined = response.data

    if (!project)
      return

    setBaseProjectData(project)

    trackSimulationResultViewed({
      project_value: project.project_value,
      system_power: project.system_power,
      system_type: project.system_type,
    })

    if (isPendingAnalysis(project)) {
      analyzingSimulation.value = true
      return
    }

    projectInactiveAndNotComplete.value = isProjectInactive(project)

    setClientStatus(project)
    setBNPLStatus(project.bnpl)
  }
  catch {
    noResultMessage.value = true
  }
  finally {
    isLoadingGuarantor.value = false
  }
}

const shoppingCartId = ref('')
const loading = ref(false)
onMounted(async () => {
  shoppingCartId.value = String(route.query.shopping_cart_id) !== 'undefined' ? String(route.query.shopping_cart_id) : ''
  await getFlagSubscriptionEnergy()
  try {
    loading.value = true
    await mountResponse()
  }
  finally {
    loading.value = false
  }
})

async function updateSimulation() {
  try {
    loading.value = true

    if (!projectResponse.value?.pre_approved_value)
      return createErrorToast('Projeto sem valor pre aprovado.')

    const approveProjectData = {
      project_id: projectResponse.value.id,
    }

    const response = await simulatorService.approve_new_down_payment(approveProjectData)

    if (response)
      router.push(`/simulation/details/${response.data}${shoppingCartId.value ? `?shopping_cart_id=${shoppingCartId.value}` : ''}`)
  }
  catch (e: any) {
    createErrorToast(e?.response?._data?.details || 'Ocorreu um erro.')
  }
  finally {
    loading.value = false
  }
}

const showContinueWithFinancingButton = computed(() =>
  ['approved', 'pending', 'necessary_install_pending_project'].includes(clientStatus.value),
)

const needChangeSimulationValue = computed(() =>
  clientStatus.value === 'pending'
  || (clientStatus.value === 'necessary_install_pending_project'
    && (projectValue.value - (projectResponse.value?.pre_approved_value ?? 0)) !== 0),
)

function handleContinueWithFinancing() {
  if (needChangeSimulationValue.value) {
    toggleSimulationUpdateModal.value = true
    return
  }

  const query = shoppingCartId.value ? `?shopping_cart_id=${shoppingCartId.value}` : ''
  router.push(`/simulation/details/${route.params.id}${query}`)
}

function formatMonth(value: number) {
  if (!value)
    return '--'
  return value / 30
}

function formatDocument(value: string) {
  if (!value)
    return '--'

  if (value.length === 14)
    return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')

  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

function recommendCustomerPage() {
  const url = 'https://cogecom.com.br/cogecom-solfacil/'
  window.open(url, '_blank')
}

const showSubscriptionEnergy = ref()

async function getFlagSubscriptionEnergy() {
  showSubscriptionEnergy.value = subscriptionEnergyFeatureFlag.value || false
}

const comboText = computed(() => {
  if (projectResponse.value?.combo_conecte_only) {
    return t('simulation.result.financing.combo_conecte_only_text')
  }
  return comboConnectEnabledForIntegrator.value || projectResponse.value?.selected_installments?.is_combo_connect
    ? t('simulation.result.financing.combo_connect_text')
    : t('simulation.result.financing.combo_easy_text')
})

// Watch for feature flag changes
watch(bnplFeatureFlag, (value) => {
  isEnabledToBNPL.value = value || false
}, { immediate: true })

watch(bnplInterestFeatureFlag, (value) => {
  isEnabledToBNPLInterest.value = value || false
}, { immediate: true })

watch(subscriptionEnergyFeatureFlag, (value) => {
  showSubscriptionEnergy.value = value || false
}, { immediate: true })

watch(comboConnectEnabledForIntegrator, () => {
  // Trigger recomputation of comboText
}, { immediate: true })

watch(financing, (newFinancing) => {
  if (newFinancing?.step === 'simulation' && newFinancing?.status === 'reproved') {
    clientStatus.value = 'reproved'
    clientApproved.value = false
  }
}, { immediate: true })

const sliders = [
  {
    image: '/images/recommend_customer_benefits.png',
    title: '',
    content: '',
  },
  {
    image: '/images/recommend_customer_how-works.png',
    title: '',
    content: '',

  },
]

const subscriptionEnergyModal = ref(false)

function redirectBNPL() {
  resetValues()
  const isProduction = import.meta.env.MODE === 'production'

  const urlProduction = `https://sso.solfacil.com.br/realms/General/protocol/openid-connect/auth?response_type=code&client_id=bnpl-parcelado&scope=openid%20profile%20email&redirect_uri=https://parcelado.solfacil.com.br/Pagamento?simulation_id=${route.params.id}`
  const urlStg = `https://stg-sso.solfacil.com.br/realms/General/protocol/openid-connect/auth?response_type=code&client_id=bnpl-parcelado&scope=openid%20profile%20email&redirect_uri=https://parcelado.solfacil.com.br/Pagamento?simulation_id=${route.params.id}`

  if (isProduction)
    window.open(urlProduction, '_blank')
  else
    window.open(urlStg, '_blank')
}

function _actionInterest() {
  toggleInterestModal.value = true
}

function openSimulationList() {
  router.push('/projects')
}

function openNewSimulation() {
  router.push(userIsOnBlockedList.value ? '/' : '/simulation/new')
}

function formattedValueInstallmentsBNPL() {
  return ((projectResponse.value?.bnpl?.pre_approved_value ?? 0) - (projectResponse.value?.bnpl?.min_entry_value ?? 0)) / (projectResponse.value?.bnpl?.max_installments ?? 0)
}

function resetValues() {
  toggleAttentionModal.value = false
  attentionCheckbox.value = false
}

function showComboConecteInverters() {
  comboConecteModalInitialTab.value = 'inverters'
  toggleComboConecteModal.value = true
}

const _showMessage = computed(() => {
  return (projectResponse.value?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_without_co_obligation && isEnabledToBNPL.value === false)
})

const _showResultInterest = computed(() => {
  return projectResponse.value?.person_type === 'PJ' || (!isEnabledToBNPL.value && !(projectResponse.value?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_without_co_obligation))
})

const _showBNPL = computed(() => {
  if (projectResponse.value?.combo_conecte_only) {
    return false
  }
  return !(projectResponse.value?.person_type === 'PJ') && ((projectResponse.value?.person_type === 'PF' && (!isEnabledToBNPL.value && (projectResponse.value?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_without_co_obligation))) || isEnabledToBNPL.value)
})

const loadings = computed(() => loading.value || isLoading.value)

const mappingErrors = computed (() => ['NOT_FOUND', 'UNAUTHORIZED', 'UNKNOWN'].includes(errorMessage.value))

const tag = ref<Tag>()

const isPendingGuarantor = computed(() => clientStatus.value === 'pending_guarantor')
watch(isPendingGuarantor, (newValue) => {
  if (newValue) {
    tag.value = {
      type: 'warning',
      text: t('result.tag.guarantor.partially_approved'),
    } as Tag
  }
}, { immediate: true })

// Reproved without guarantor because the battery-only financing limit was reached: replaces the default reproved message
const isOnlyBatteryLimitExceeded = computed(() =>
  clientStatus.value === 'reproved'
  && !analyzingSimulation.value
  && !projectResponse.value?.guarantor
  && projectResponse.value?.decision_reason === PROJECT_DECISION_REASON.ONLY_BATTERY_LIMIT_EXCEEDED,
)

const isGuarantorAndApproved = computed(() => projectResponse.value?.guarantor && clientStatus.value === 'approved')
watch(isGuarantorAndApproved, (newValue) => {
  if (newValue) {
    tag.value = {
      type: 'positive',
      text: t('result.tag.guarantor.total_approved'),
    } as Tag
  }
}, { immediate: true })

watch(clientStatus, (newValue) => {
  if (newValue === 'reproved') {
    tag.value = {
      type: 'negative',
      text: t(`header.simulation.status.reproved.text`),
    } as Tag
  }
}, { immediate: true })

const downPaymentMountValue = computed(() => {
  const preApprovedValue = projectResponse.value?.pre_approved_value ?? 0
  const calculatedValue = projectValue.value - preApprovedValue
  return formatMoney(needChangeSimulationValue.value ? calculatedValue : projectResponse.value?.down_payment)
})

const hasInstallmentConditions = computed(() => {
  const conditions = projectResponse.value?.installments_conditions
  return conditions?.suggested_installments && conditions?.suggested_installments_value
})

const simulationValueList = computed(() => {
  return [
    hasInstallmentConditions.value
      ? {
          icon: IconAccountBalanceWallet,
          title: t('simulation.result.financing.installment_value'),
          subtitle: isPendingGuarantor.value
            ? '-'
            : projectResponse?.value?.installments_conditions?.suggested_installments_value
              ? `${projectResponse.value?.installments_conditions?.suggested_installments}x
            ${t('simulation.result.financing.of')}
            ${formatMoney(projectResponse?.value?.installments_conditions?.suggested_installments_value)}`
              : '-',
        }
      : null,
    {
      icon: IconAutoPause,
      title: t('project.max_installments'),
      subtitle: isPendingGuarantor.value
        ? '-'
        : `${t('simulation.result.financing.to')} ${projectResponse.value?.max_installments}x`,
    },
    {
      icon: IconCalendarMonth,
      title: t('project.shortage'),
      subtitle: isPendingGuarantor.value
        ? '-'
        : `${t('simulation.result.financing.to')} ${formatMonth(projectResponse.value?.max_grace_period || 0)} ${t('simulation.result.financing.months')}`,
    },
    {
      icon: IconPaid,
      title: t('project.entry'),
      subtitle: `${isPendingGuarantor.value ? '-' : downPaymentMountValue.value}`,
      alert: needChangeSimulationValue.value
        ? {
            icon: IconWarning,
            title: t('project.down_payment'),
            type: 'warning',
          }
        : null,
    },
    {
      icon: IconSolarPower,
      title: t('project.kit_acquisition'),
      subtitle: projectResponse.value?.combo_conecte_only
        ? t('project.solfacil_store_with_asterisk')
        : t('project.solfacil_store_or_external'),
    },
  ]
})

const _bnplValueList = computed(() => {
  return [
    hasInstallmentConditions.value
      ? {
          icon: IconAccountBalanceWallet,
          title: t('simulation.result.financing.installment_value'),
          subtitle: formatMoney(formattedValueInstallmentsBNPL()),
        }
      : null,
    {
      icon: IconAutoPause,
      title: t('project.max_installments'),
      subtitle: projectResponse.value?.bnpl?.max_installments
        ? `${t('simulation.result.financing.to')} ${projectResponse.value?.bnpl?.max_installments}x`
        : '-',
    },
    {
      icon: IconCalendarMonth,
      title: t('project.shortage'),
      subtitle: t('simulation.result.bnpl.without_period'),
    },
    {
      icon: IconPaid,
      title: t('project.entry'),
      subtitle: projectResponse.value?.bnpl?.min_entry_value
        ? `${formatMoney(projectResponse.value?.bnpl?.min_entry_value)}`
        : '-',
    },
    {
      icon: IconSolarPower,
      title: t('project.kit_acquisition'),
      subtitle: projectResponse.value?.combo_conecte_only
        ? t('project.solfacil_store_with_asterisk')
        : t('project.solfacil_store'),
    },

  ]
})

function goToHomePage() {
  window.open('https://solfacil.com.br/financie/', '_blank')
}

const enableCombo = computed(() => {
  return projectResponse.value?.has_combo_enabled !== false
})
</script>

<template>
  <div class="result_page" data-testid="result-page">
    <NavigationHeaderBar
      stage="simulation"
      :tag-page="tag"
      :title="projectResponse?.project_name"
      :is-inactive-and-not-complete="projectInactiveAndNotComplete"
      :financing="financing"
    />
    <div v-if="loadings" class="container">
      <div class=" card-container flex flex-col justify-between w-full">
        <span class="loader-project" />
      </div>
    </div>
    <div v-if="!loadings" class="container">
      <template v-if="isError && errorMessage !== 'NOT_FOUND' && mappingErrors">
        <SharedErrorsCheckAccess :error-message="errorMessage" data-testid="error-message" />
      </template>
      <template v-else>
        <SolAlert
          v-if="clientStatus === 'necessary_install_pending_project'"
          id="title"
          class="mb-4xs md:site:mb-10"
          :title="$t('simulation.info_risk.necessary_install_pending_project_title')"
          feedback="warning"
          data-testid="necessary-install-feedback"
        >
          <div>
            <p>{{ $t('simulation.info_risk.necessary_install_pending_project_description') }}</p>
          </div>
        </SolAlert>
        <SolAlert
          v-if="userIsOnBlockedList"
          id="error-feedback"
          :title="t('blocked_list.alert.title')"
          feedback="error"
          :action="t('blocked_list.alert.action')"
          class="mb-4xs"
          @click="openWhatsApp('5511912386374', t('blocked_list.whatsapp.message'))"
        >
          {{ t('blocked_list.alert.description') }}
        </SolAlert>
        <!-- result card -->
        <div v-if="projectResponse" class="header-card-result" data-testid="result-card">
          <span class="fonts-body-large-bold text-neutral-low-dark" data-testid="result-title">
            {{ t('simulation.result.data.title') }}
          </span>

          <div
            class="card-result-content"
            data-testid="result-subtitle"
          >
            <div class="text-container">
              <div class="text-content">
                <span class="title">
                  {{ t('simulation.result.data.name') }}
                </span>
                <span class="description" data-testid="project-name-value">
                  {{ conventionalNameFormat(projectResponse?.project_name) }}
                </span>
              </div>

              <div class="flex flex-col items-start gap-1">
                <span class="fonts-body-medium-regular text-neutral-low-light">
                  {{ projectResponse?.person_type === 'PF' ? t('simulation.result.data.type.cpf') : t('simulation.result.data.type.cnpj') }}
                </span>
                <span class="fonts-body-large-regular text-neutral-low-medium whitespace-nowrap" data-testid="document-value">
                  {{ formatDocument(projectResponse?.document) }}
                </span>
              </div>
            </div>

            <div class="px-nano h-13 hidden md:site:block">
              <SolDivider thickness="x-small" orientation="vertical" />
            </div>

            <div class="text-container">
              <div class="text-content">
                <span class="title">
                  {{ t('simulation.result.data.bill') }}
                </span>
                <span class="description" data-testid="bill-value">
                  {{ formatMoney(projectResponse.bill_value) }}
                </span>
              </div>

              <div class="text-content">
                <span class="title">
                  {{ t('simulation.result.data.value') }}
                </span>
                <span class="description" data-testid="project-value-amount">
                  {{ formatMoney(projectResponse?.project_value) }}
                </span>
              </div>

              <div class="flex flex-col items-start gap-1">
                <span class="fonts-body-medium-regular text-neutral-low-light">
                  {{ t('simulation.result.data.down_payment') }}
                </span>
                <span class="fonts-body-large-regular text-neutral-low-medium whitespace-nowrap" data-testid="down-payment-value">
                  {{ Number(projectResponse.down_payment) === 0 ? '-' : formatMoney(projectResponse.down_payment) }}
                </span>
              </div>
            </div>

            <div class="px-nano h-13 hidden md:site:block">
              <SolDivider thickness="x-small" orientation="vertical" />
            </div>

            <div class="text-container">
              <div class="text-content">
                <span class="title">
                  {{ isOnlyBatteryProject ? t('simulation.result.data.batteryCapacity') : t('simulation.result.data.systemPower') }}
                </span>
                <span class="description" data-testid="system-power-value">
                  {{ projectResponse?.system_power.toFixed(2) }}
                </span>
              </div>

              <div v-if="isKnownProjectSystemType" class="flex flex-col items-start gap-1">
                <span class="fonts-body-medium-regular text-neutral-low-light">
                  {{ t('simulation.result.data.system_type') }}
                </span>
                <span class="fonts-body-large-regular text-neutral-low-medium whitespace-nowrap" data-testid="system-type-value">
                  {{ t(`simulation.newPage.form.system_type.${projectResponse?.system_type}`) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- options card end values -->
        <div v-if="!isLoadingGuarantor && projectResponse" class="w-full">
          <div>
            <div
              v-if="((clientStatus === 'reproved' && !analyzingSimulation) || analyzingSimulation) || noResultMessage"
              class="result-card card-container"
              data-testid="messages-container"
            >
              <!-- Rejected customer -->
              <div
                v-if="clientStatus === 'reproved' && !analyzingSimulation && !projectResponse?.guarantor"
                class="flex flex-col"
                data-testid="rejected-customer"
              >
                <div class="flex pb-2 mb-2 border-highlight-light border-b">
                  <img
                    src="/images/customer-rejected.svg"
                    :alt="t('simulation.result.bnpl.customerRejected.altImg')"
                    class="object-cover w-full h-full rounded-lg w-[55px]"
                    data-testid="rejected-customer-img"
                  >
                  <h2 class="flex gap-1 items-center justify-center ml-4">
                    <span class="text-[24px] font-highlight text-feedback-negative-pure" data-testid="rejected-customer-title">{{ isOnlyBatteryLimitExceeded ? t('simulation.result.bnpl.customerRejected.onlyBatteryLimit.title') : t('simulation.result.bnpl.customerRejected.title') }}</span>
                  </h2>
                </div>
                <p v-if="isOnlyBatteryLimitExceeded" class="pt-2 fonts-body-large-regular text-neutral-low-light" data-testid="rejected-customer-description">
                  {{ t('simulation.result.bnpl.customerRejected.onlyBatteryLimit.description') }}
                </p>
                <template v-else>
                  <p class="pt-2 mb-4 fonts-body-large-bold text-neutral-low-dark" data-testid="rejected-customer-description-title">
                    {{ t('simulation.result.bnpl.customerRejected.description.title') }}
                  </p>
                  <div class="flex gap-2 mb-2">
                    <IconArrowRightAlt class="text-neutral-low-light min-w-[14px] w-[14px]" />
                    <span class="fonts-body-large-regular text-neutral-low-light" data-testid="rejected-customer-description-action-1">{{ t('simulation.result.bnpl.customerRejected.description.actions[0]') }}</span>
                  </div>
                  <div class="flex gap-2">
                    <IconArrowRightAlt class="text-neutral-low-light min-w-[14px] w-[14px]" />
                    <span class="fonts-body-large-regular text-neutral-low-light" data-testid="rejected-customer-description-action-2">{{ t('simulation.result.bnpl.customerRejected.description.actions[1]') }}</span>
                  </div>
                </template>
              </div>
              <!-- Close Rejected customer -->

              <!-- Rejected Guarantor -->
              <div
                v-if="clientStatus === 'reproved' && projectResponse?.guarantor"
                class="flex flex-col"
                data-testid="rejected-guarantor"
              >
                <div class="flex pb-2 mb-2 border-highlight-light border-b">
                  <img
                    src="/images/customer-rejected.svg"
                    :alt="t('simulation.result.bnpl.customerRejected.altImg')"
                    class="object-cover w-full h-full rounded-lg w-[55px]"
                    data-testid="rejected-guarantor-img"
                  >
                  <h2 class="flex gap-1 items-center justify-center ml-4">
                    <span class="text-[24px] font-highlight text-feedback-negative-pure" data-testid="rejected-guarantor-title">{{ t('simulation.result.bnpl.customerRejectedGuarantor.title') }}</span>
                  </h2>
                </div>
                <p class="pt-2 mb-4 fonts-body-large-bold text-neutral-low-dark" data-testid="rejected-guarantor-description-title">
                  {{ t('simulation.result.bnpl.customerRejectedGuarantor.description.title') }}
                </p>
                <div class="flex gap-2 mb-2">
                  <IconArrowRightAlt class="text-neutral-low-light min-w-[14px] w-[14px]" />
                  <span class="fonts-body-large-regular text-neutral-low-light" data-testid="rejected-guarantor-description-action-1">{{ t('simulation.result.bnpl.customerRejectedGuarantor.description.actions[0]') }}</span>
                </div>
                <div class="flex gap-2">
                  <IconArrowRightAlt class="text-neutral-low-light min-w-[14px] w-[14px]" />
                  <span class="fonts-body-large-bold text-neutral-low-light" data-testid="rejected-guarantor-description-action-2">{{ t('simulation.result.bnpl.customerRejectedGuarantor.description.actions[1]') }}</span>
                </div>
              </div>
              <!-- Close Rejected Guarantor -->

              <!-- Message no results -->
              <div v-if="noResultMessage" data-testid="no-results-message">
                <SolEmptyState
                  id="message-results"
                  class="w-full max-w-full"
                  :title="t('simulation.result.bnpl.messages.noResults.title')"
                  :subtitle="t('simulation.result.bnpl.messages.noResults.description')"
                  variant="empty-list"
                  data-testid="no-results-empty-state"
                />
                <div class="flex flex-row justify-center gap-2">
                  <SolButton
                    id="no-results"
                    variant="primary"
                    data-testid="no-results-button"
                    @click="mountResponse"
                  >
                    {{ t('simulation.result.bnpl.messages.noResults.textButton') }}
                  </SolButton>
                </div>
              </div>
              <!-- Close Message no results -->

              <!-- Message Analyzing Simulation -->
              <div v-if="analyzingSimulation" data-testid="analyzing-simulation-message">
                <SolEmptyState
                  id="analyzing-simulation"
                  class="w-full max-w-full"
                  :title="t('simulation.result.bnpl.messages.analyzingSimulation.title')"
                  :subtitle="t('simulation.result.bnpl.messages.analyzingSimulation.description')"
                  variant="search"
                  data-testid="analyzing-simulation-empty-state"
                />

                <div class="flex flex-row justify-center gap-2">
                  <SolButton
                    id="list"
                    variant="secondary"
                    data-testid="analyzing-simulation-list-button"
                    @click="openSimulationList()"
                  >
                    {{ t('simulation.result.bnpl.messages.analyzingSimulation.textListButton') }}
                  </SolButton>
                  <SolButton
                    v-if="auth?.person.partner?.permission.financing"
                    id="new-simulation"
                    variant="primary"
                    data-testid="analyzing-simulation-new-button"
                    :disabled="userIsOnBlockedList"
                    :loading="isLoadingPartner"
                    @click="openNewSimulation()"
                  >
                    {{ t('simulation.result.bnpl.messages.analyzingSimulation.textNewSimulationButton') }}
                  </SolButton>
                </div>
              </div>
              <!-- Close Message Analyzing Simulation -->
            </div>

            <!-- Financing, BNPL and installments -->
            <div
              v-if="!analyzingSimulation && !(clientStatus === 'reproved') && !noResultMessage"
              class="flex flex-wrap md:system:flex-nowrap gap-0 md:system:gap-8 justify-center items-center"
              data-testid="financing-and-installments"
            >
              <div class="card-container-wrapper combo-conecte-only">
                <!-- Financing new layout -->
                <div class="card-container-new" data-testid="financing-card">
                  <div class="card-content">
                    <div class="card-header-container">
                      <div class="card-header-content">
                        <img
                          src="/icons/simulation-result-financing.svg"
                        >
                        <div class="card-header-text">
                          <span class="card-header-text-title" data-testid="financing-title">
                            {{ t('simulation.result.financing.title') }}
                          </span>
                          <span class="card-header-text-subtitle ">
                            {{ t('simulation.result.financing.subtitle') }}
                          </span>
                        </div>
                      </div>
                      <div class="card-header-tag">
                        <SolTag
                          v-if="isPendingGuarantor"
                          id="include_guarantor_tag"
                          variant="fill"
                          type="warning"
                          class="flex gap-1"
                          data-testid="include-guarantor-tag"
                        >
                          <IconAddPerson />
                          <span class="font-bold">{{ t('result.tag.guarantor.pending') }}</span>
                        </SolTag>
                        <SolTag
                          v-if="isGuarantorAndApproved"
                          id="include_guarantor_tag"
                          variant="fill"
                          type="positive"
                          class="flex gap-1"
                          data-testid="include-guarantor-approved-tag"
                        >
                          <IconCheck />
                          <span class="font-regular">{{ t('result.tag.guarantor.approved') }}</span>
                        </SolTag>
                      </div>
                    </div>
                    <template v-if="clientApproved">
                      <div class="dotted-line-container">
                        <div class="dotted-line" />
                        <!-- tag combo conecte only aqui1 -->
                        <span
                          v-if="enableCombo"
                          :class="{ 'combo-conecte-only-tag-text': projectResponse?.combo_conecte_only }"
                          class="dotted-line-text"
                        >
                          {{ comboText }}
                        </span>
                      </div>
                      <div class="financing-value-container">
                        <span class="financing-value-title">
                          {{ isPendingGuarantor ? 'Valor solicitado' : t('simulation.result.financing.pre_approved_value') }}
                        </span>
                        <div class="financing-value-content">
                          <span class="currency-symbol">R$</span>
                          <span class="approved-value" data-testid="pre-approved-value">
                            {{ formatOnlyMoneyValue(projectResponse?.pre_approved_value) }}
                          </span>
                        </div>
                      </div>
                      <div
                        class="financing-info-container"
                      >
                        <IncludeGuarantor
                          v-if="isPendingGuarantor"
                          v-model:is-loading="isLoadingGuarantor"
                          :refetch-project="mountResponse"
                          :set-false-loading-guarantor="setFalseLoadingGuarantor"
                          class="mt-6"
                          data-testid="include-guarantor"
                        />
                        <div v-else class="w-full">
                          <SolButton
                            v-if="showContinueWithFinancingButton"
                            id="follow-financing"
                            :disabled="projectInactiveAndNotComplete"
                            variant="primary"
                            on-color
                            class="w-full"
                            data-testid="follow-financing-button"
                            @click="handleContinueWithFinancing()"
                          >
                            <template #icon:left>
                              <IconPaid />
                            </template>
                            {{ projectResponse?.combo_conecte_only
                              ? t('simulation.result.financing.combo_conecte_only.button_text')
                              : t('simulation.result.bnpl.buttons.textFollowFinancing') }}
                          </SolButton>
                          <SolButton
                            v-if="!clientApproved && auth?.person.partner?.permission.financing"
                            id="go"
                            class="mt-6"
                            data-testid="go-new-simulation-button"
                            :disabled="userIsOnBlockedList"
                            :loading="isLoadingPartner"
                            @click="openNewSimulation()"
                          >
                            {{ t('simulation.new') }}
                          </SolButton>
                        </div>

                        <div class="simulation-values-container">
                          <div class="simulation-values-content">
                            <template v-for="(item, index) in simulationValueList" :key="index">
                              <div v-if="item" class="simulation-values-list">
                                <div
                                  class="simulation-values-item"
                                >
                                  <component
                                    :is="item.icon"
                                    class="simulation-values-item-icon"
                                    aria-hidden="true"
                                  />
                                  <span class="simulation-values-item-title">
                                    {{ item.title }}
                                  </span>
                                </div>
                                <div class="simulation-values-alert-container">
                                  <div v-if="item?.alert?.icon" class="simulation-values-alert-content">
                                    <div
                                      class="alert-title"
                                      :class="{
                                        '-warning': item.alert.type === 'warning',
                                        '-positive': item.alert.type === 'positive',
                                      }"
                                    >
                                      <component
                                        :is="item?.alert?.icon"
                                        class="w-6 h-6"
                                        aria-hidden="true"
                                      />
                                      <span>
                                        {{ item?.alert?.title }}
                                      </span>
                                    </div>
                                  </div>
                                  <span class="simulation-values-alert-subtitle">
                                    {{ item.subtitle }}
                                  </span>
                                </div>
                              </div>
                              <div v-if="index !== simulationValueList.length - 1" class="simulation-values-dotted-line" />
                            </template>
                          </div>
                          <SolLink
                            v-if="!projectResponse?.combo_conecte_only"
                            id="show-financing-know-more"
                            size="medium"
                            @click="goToHomePage()"
                          >
                            <template #icon>
                              <IconLink />
                            </template>
                            {{ t('financing.button.know_more') }}
                          </SolLink>
                          <SolLink
                            v-else
                            id="show-combo-conecte-know-more"
                            size="medium"
                            @click="toggleComboConecteModal = true"
                          >
                            <template #icon>
                              <IconLink />
                            </template>
                            {{ t('simulation.result.financing.combo_conecte_only.know_more') }}
                          </SolLink>
                          <p
                            v-if="projectResponse?.combo_conecte_only"
                            class="combo-conecte-consult-inverters-text"
                          >
                            <span>{{ t('simulation.result.financing.combo_conecte_only.consult_inverters.prefix') }}</span>
                            <a
                              id="show-combo-conecte-inverters"
                              class="combo-conecte-consult-inverters-link"
                              data-testid="show-combo-conecte-inverters-link"
                              @click="showComboConecteInverters"
                            >
                              {{ t('simulation.result.financing.combo_conecte_only.consult_inverters.link_text') }}
                            </a>
                            <span>{{ t('simulation.result.financing.combo_conecte_only.consult_inverters.suffix') }}</span>
                          </p>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- BNPL new layout -->
                <!-- <div
                  v-if="isEnabledToBNPLInterest && !projectResponse?.combo_conecte_only"
                  class="card-container-new"
                  data-testid="bnpl-card"
                >
                  <div class="card-content">
                    <SimulationResultInterest
                      v-if="showResultInterest && clientStatusBNPL !== 'reproved'"
                      data-testid="period-payment"
                      @action-interest="actionInterest()"
                    />
                    <template
                      v-if="showBNPL && clientStatusBNPL !== 'reproved'"
                    >
                      <div class="card-header-container">
                        <div class="card-header-content">
                          <img
                            src="/icons/simulation-result-bnpl.svg"
                          >
                          <div class="card-header-text ">
                            <span class="card-header-text-title">
                              <div>{{ t('simulation.result.bnpl.title') }}</div>
                            </span>
                            <span class="card-header-text-subtitle">
                              {{ t('simulation.result.bnpl.subtitle') }}
                            </span>
                          </div>
                        </div>
                        <div class="card-header-tag">
                          <SolTag
                            v-if="showMessage"
                            id="result-bpnl"
                            class="tag-secondary m-0"
                            :text="t('simulation.result.bnpl.shortly.flag')"
                            variant="fill"
                            size="small"
                            data-testid="bnpl-tag"
                          />
                        </div>
                      </div>

                      <div class="dotted-line-container">
                        <div class="dotted-line" />
                        <span class="dotted-line-text" white="true">
                          Sem necessidade do cartão de crédito
                        </span>
                      </div>
                      <div
                        class="financing-value-container"
                      >
                        <span class="financing-value-title">
                          {{ isPendingGuarantor ? 'Valor solicitado' : t('simulation.result.financing.pre_approved_value') }}
                        </span>
                        <div class="financing-value-content">
                          <span class="currency-symbol">R$</span>
                          <span class="approved-value" data-testid="pre-approved-value">
                            {{ formatOnlyMoneyValue(projectResponse?.pre_approved_value) }}
                          </span>
                        </div>
                      </div>
                      <div
                        v-if="projectResponse.person_type === 'PF'"
                        class="w-full"
                        data-testid="bnpl-buttons-container"
                      >
                        <SolButton
                          v-if="
                            isEnabledToBNPL && (projectResponse?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_without_co_obligation || projectResponse?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_with_co_obligation)"
                          id="follow-installment"
                          variant="secondary"
                          class="w-full"
                          :disabled="projectInactiveAndNotComplete"
                          data-testid="follow-installment-button"
                          @click="projectResponse?.bnpl?.decision_pre_analysis === analysisStatusBNPL.approved_without_co_obligation ? redirectBNPL() : toggleAttentionModal = true"
                        >
                          {{ t('simulation.result.bnpl.buttons.textFollowInstallment') }}
                          <template #icon:right>
                            <IconOpenInNew />
                          </template>
                        </SolButton>
                      </div>
                      <div class="simulation-values-container">
                        <div class="simulation-values-content">
                          <template v-for="(item, index) in bnplValueList" :key="index">
                            <div v-if="item" class="simulation-values-list">
                              <div
                                class="simulation-values-item"
                              >
                                <component
                                  :is="item.icon"
                                  class="simulation-values-item-icon"
                                  aria-hidden="true"
                                />
                                <span class="simulation-values-item-title">
                                  {{ item.title }}
                                </span>
                              </div>
                              <div class="simulation-values-alert-container">
                                <span class="simulation-values-alert-subtitle">
                                  {{ item.subtitle }}
                                </span>
                              </div>
                            </div>
                            <div v-if="index !== bnplValueList.length - 1" class="simulation-values-dotted-line" />
                          </template>
                        </div>
                        <SolLink
                          id="show-financing-know-more"
                          size="medium"
                          @click="actionInterest()"
                        >
                          <template #icon>
                            <IconLink />
                          </template>
                          {{ t('financing.button.know_more') }}
                        </SolLink>
                      </div>
                    </template>
                    <template v-if="clientStatusBNPL === 'reproved'">
                      <div class="card-header-container flex-col h-full">
                        <div class="card-header-content">
                          <img
                            src="/icons/simulation-result-bnpl.svg"
                          >
                          <div class="card-header-text ">
                            <span class="card-header-text-title">
                              <div>{{ t('simulation.result.bnpl.title') }}</div>
                            </span>
                            <span class="card-header-text-subtitle">
                              {{ t('result.bnpl.description_card') }}
                            </span>
                          </div>
                        </div>
                        <div
                          class="flex flex-col items-center h-full w-full justify-center"
                        >
                          <img
                            src="/images/customer-rejected.svg"
                            :alt="t('simulation.result.bnpl.customerRejected.altImg')"
                            class="w-full rounded-lg w-[193px]"
                          >
                          <h2 class="flex gap-1 items-center justify-center mt-4">
                            <span class="text-[24px] font-highlight">{{ t('simulation.result.bnpl.customerRejected.title') }}</span>
                          </h2>
                          <p class="text-[16px] text-neutral-low-light pt-2 pb-6 text-center">
                            {{ t('result.bnpl.reproved.description') }}
                          </p>
                        </div>
                      </div>
                    </template>
                  </div>
                </div> -->
              </div>
            </div>
            <!-- Close Financing and installments -->
          </div>

          <div class="button-container" data-testid="button-container">
            <SolButton
              v-if="clientStatus !== 'reproved'"
              id="back-button"
              variant="secondary"
              size="medium"
              data-testid="back-button"
              @click="openSimulationList()"
            >
              {{ t('buttons.back') }}
            </SolButton>

            <SolButton
              v-if="clientStatus === 'reproved' && !projectResponse?.guarantor"
              id="back-button"
              variant="primary"
              size="medium"
              data-testid="go-financing-button"
              @click="openSimulationList()"
            >
              {{ t('buttons.go_financing') }}
            </SolButton>

            <SolButton
              v-if="clientStatus === 'reproved' && projectResponse?.guarantor"
              id="back-new-page"
              variant="primary"
              size="medium"
              data-testid="go-new-page-button"
              :disabled="userIsOnBlockedList"
              :loading="isLoadingPartner"
              @click="openNewSimulation()"
            >
              {{ t('buttons.go_new_page') }}
            </SolButton>
          </div>
        </div>
        <div v-if="isLoadingGuarantor" class="my-4 card-container w-full h-96 flex justify-center" data-testid="loading-guarantor">
          <Loading :messages="loadingMessages" />
        </div>
      </template>
    </div>
  </div>
  <SolOnboarding
    id="subscription_energy_modal"
    :is-open="subscriptionEnergyModal"
    :sliders="sliders"
    :final-button-text="$t('subscriptionEnergy.recommend_customer')"
    data-testid="subscription-energy-modal"
    @action:finish="recommendCustomerPage()"
    @update:is-open="subscriptionEnergyModal = $event"
  />

  <!-- Modais -->
  <SolModal
    id="edit-modal"
    title="Atenção!"
    data-testid="edit-modal"
    :is-open="toggleSimulationUpdateModal"
    action-secondary-text="Cancelar"
    @close="closeUpdateSimulationModal"
    @action:secondary="closeUpdateSimulationModal"
  >
    <div
      v-if="projectResponse?.pre_approved_value"
      class="fonts-heading-h4 text-neutral-low-medium flex flex-col gap-5"
      data-testid="pre-approved-value"
    >
      <span>
        Para seguir com este financiamento é necessário ajustar o valor da entrada do projeto para
        <span class="text-brand-secondary-dark">{{ formatMoney(projectValue - projectResponse?.pre_approved_value) }}</span>.
      </span>
      <span>
        Deseja seguir com o novo valor de entrada?
      </span>
    </div>

    <template #action-primary>
      <SolButton
        id="btn-simulate"
        size="medium"
        variant="primary"
        data-testid="btn-simulate"
        @click="updateSimulation()"
      >
        Atualizar valor da entrada
      </SolButton>
    </template>
  </SolModal>
  <!-- Attention -->
  <SolModal
    id="attention-modal"
    :is-open="toggleAttentionModal"
    :size="{
      desktop: 'medium',
      mobile: 'bottom-sheet',
    }"
    :title="t('simulation.result.bnpl.modals.attention.title')"
    :action-secondary-text="t('simulation.result.bnpl.modals.attention.textCancelButton')"
    data-testid="attention-modal"
    @action:secondary="resetValues()"
    @close="resetValues()"
  >
    <p> {{ t('simulation.result.bnpl.modals.attention.description') }} </p>

    <div>
      <SolCheckbox
        id="attention-text-checkbox"
        v-model="attentionCheckbox"
        class="mt-2"
        name="attention-text-checkbox"
        :label="t('simulation.result.bnpl.modals.attention.textCheckbox')"
        value="attentionOK"
        data-testid="attention-checkbox"
        @change="attentionCheckbox = !attentionCheckbox"
      />
    </div>

    <template #action-primary>
      <SolButton
        id="btn-financing"
        size="medium"
        variant="secondary"
        :disabled="!attentionCheckbox || projectInactiveAndNotComplete"
        data-testid="btn-financing"
        @click="redirectBNPL"
      >
        {{ t('simulation.result.bnpl.buttons.textFollowInstallment') }}

        <template #icon:right>
          <IconOpenInNew />
        </template>
      </SolButton>
    </template>
  </SolModal>
  <!-- Close Attention -->

  <!-- Interest -->
  <SolModal
    id="interest-modal"
    :is-open="toggleInterestModal"
    :size="{
      desktop: 'medium',
      mobile: 'bottom-sheet',
    }"
    :title="t('simulation.result.bnpl.modals.interest.title')"
    :action-primary-text="t('simulation.result.bnpl.modals.interest.textButton')"
    data-testid="interest-modal"
    @action:primary="toggleInterestModal = false"
    @close="toggleInterestModal = false"
  >
    <div class="flex mb-6 gap-8">
      <div class="flex flex-col items-center justify-center gap-3">
        <img src="/images/percent.svg" :alt="t('simulation.result.bnpl.modals.interest.altImgOption1')" data-testid="interest-option1-img">
        <span class="text-center font-highlight text-neutral-low-medium" data-testid="interest-option1">{{ t('simulation.result.bnpl.modals.interest.option1') }}</span>
      </div>
      <div class="flex flex-col items-center justify-center gap-3">
        <img src="/images/installments.svg" :alt="t('simulation.result.bnpl.modals.interest.altImgOption2')" data-testid="interest-option2-img">
        <span class="text-center font-highlight text-neutral-low-medium" data-testid="interest-option2">{{ t('simulation.result.bnpl.modals.interest.option2') }}</span>
      </div>
      <div class="flex flex-col items-center justify-center gap-3">
        <img src="/images/solar-plate.svg" :alt="t('simulation.result.bnpl.modals.interest.altImgOption3')" data-testid="interest-option3-img">
        <span class="text-center font-highlight text-neutral-low-medium" data-testid="interest-option3">{{ t('simulation.result.bnpl.modals.interest.option3') }}</span>
      </div>
    </div>
    <p class="text-center text-neutral-low-medium" data-testid="interest-description">
      {{ t('simulation.result.bnpl.modals.interest.description') }}
    </p>
  </SolModal>
  <!-- Close Interest -->

  <!-- Combo Conecte Only Modal -->
  <ComboConecteModal
    v-model:is-open="toggleComboConecteModal"
    :project="projectResponse"
    :initial-tab="comboConecteModalInitialTab"
  />
  <!-- Close Combo Conecte Only Modal -->
  <!-- Close Modais -->
</template>

<style lang="scss">
.result_page {
  @apply flex flex-col items-start gap-4 pt-2xs px-micro;
    @apply md:site:px-2xs;
  .container {
    @apply flex flex-col items-start gap-4;
    .header-card-result {
      @apply flex flex-col px-2xs py-4xs gap-4 items-start w-full;
      @apply rounded-md bg-neutral-high-pure;

      .card-result-content {
        @apply flex items-start gap-8 justify-around w-full flex-wrap md:site:flex-nowrap;
        .text-container {
          @apply flex items-start gap-6 justify-start md:site:justify-around w-full flex-wrap md:site:flex-nowrap;
          .text-content {
            @apply flex flex-col items-start gap-1;
            .title {
              @apply fonts-body-medium-regular text-neutral-low-light whitespace-nowrap;
            }
            .description {
              @apply fonts-body-large-regular text-neutral-low-medium;
            }
          }
        }
      }
    }

    .result-card {
      @apply py-4xs px-2xs flex flex-col justify-between w-full ;
    }

    .card-banner {
      background-image: url('/images/simulation-banner.jpg');
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      @apply px-5 py-3;

      > div {
        background-color: rgba(0, 0, 0, 0.8);
        @apply px-4 py-2 mx-1 rounded-md;

        .title-banner {
          font-size: 20px;
          @apply flex justify-start flex-wrap gap-1 text-xs font-bold text-neutral-high-light;
        }

        .subtitle {
          font-size: 14px;
          @apply text-neutral-high-light mt-1;
        }
      }
    }

    .button-container {
      @apply flex flex-col w-full gap-4 mt-2xs justify-between;

      > .button-result {
        @apply w-full;

        @screen md:site {
          @apply self-end w-auto;
        }
      }

      @screen md:site {
        @apply flex-row;
      }

    }
    .title-banner {
      @apply flex justify-between items-center gap-2;
      @apply fonts-body-large-bold;
      @apply text-neutral-low-dark;
    }

    .subtitle {
      @apply fonts-subtitle-small text-neutral-low-light;
    }
  }
  .result-content{
    @apply flex flex-wrap gap-6;
    @apply md:site:flex-nowrap;
  }

  #tag-result-bpnl {
    @apply bg-brand-secondary-medium;
  }

  .not-found{
    height: 96px;
    width: 96px;
  }

  .loader-project {
    @apply h-96 rounded-lg mt-5;
    @apply top-0 right-0 bottom-0 left-0 z-50;
    background: rgba(#fff, 0.9)
      url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat
      center center;
  }

  .financial-item {
    @apply flex justify-between py-nano border-b-neutral-high-medium border-b-1;
  }

  .financial-item-colored {
    @apply flex justify-between py-nano border-b-brand-secondary-pure border-b-1;
  }

  .financial-item-transparent {
    @apply flex justify-between py-nano border-transparent border-b-1;
  }

  .financial-item-colored[pending="true"] {
    @apply flex justify-between py-nano border-b-feedback-warning-dark border-b-1;
  }

  .financial-item-colored[pending_guarantor="true"] {
    @apply flex justify-between py-nano border-b-feedback-warning-dark border-b-1;
  }

  .approved-value-container {
    @apply rounded-lg py-2xs px-6 flex flex-col text-center items-center justify-center;
    @apply bg-feedback-positive-light text-feedback-positive-dark w-full;

  }

  .approved-value-container-bnpl{
    background-color: #f2ffd8
  }
  .approved-value-container[pending="true"]{
    @apply rounded-lg py-2xs px-sm flex flex-col text-center items-center justify-center;
    @apply bg-feedback-warning-light text-feedback-warning-dark;

  }

  .installments-container{
    @apply w-full;

    > .sol-accordion-core[open]>.summary{
      @apply bg-neutral-high-pure;
    }

    >.sol-accordion-core[open]>.accordion-content{
      @apply bg-neutral-high-pure;
    }
  }

  .card-container{
    @apply px-4xs my-6 bg-neutral-high-pure rounded-lg;

    @screen md:site {
      @apply px-6;
    }
  }
  .result-card--reproved {
    @apply bg-neutral-high-pure mb-2xs rounded-lg;

  }
}

.sol-overlay-core {
  .content {
    @apply h-full;

    .wrap {
      @apply h-full w-full;

      .image {
        @apply object-contain h-full mb-quark;
      }
    }

    .slider {
      @apply mb-xs;
    }
  }
}

#button-alert-action-restrictions-title-feedback {
  @apply w-[170px];
}

#empty-state-message-results,
#empty-state-analyzing-simulation {
  h1 {
    font-weight: 500;
    @apply font-highlight text-[24px];
  }
}
.card-container-wrapper {
  @apply flex flex-wrap gap-16 w-full;
  @apply md:site:gap-6 mt-7 md:site:mt-auto;

  &.combo-conecte-only {
    @apply max-w-full md:site:w-[660px] justify-center items-center;
  }

  .card-container-new {
    @apply flex flex-col flex-1 justify-center items-center;
    .card-content {
      @apply flex flex-col items-center gap-6 p-6 rounded-md bg-neutral-high-pure w-full h-full;
      .card-header-container {
        @apply flex items-start justify-between w-full;
        .card-header-content {
          @apply flex flex-col items-start gap-1 flex-1;
          .card-header-text {
            @apply flex flex-col items-start gap-1 self-stretch;
            .card-header-text-title {
              @apply fonts-heading-h4 text-neutral-low-dark;
            }
            .card-header-text-subtitle {
              @apply fonts-body-medium-regular text-neutral-low-light;
            }
          }
          .card-header-tag {
            @apply flex items-center gap-1 flex-shrink-0;
          }
        }
      }
      .dotted-line-container {
        @apply relative w-full my-3;

        .dotted-line {
          @apply border-t-brand-primary-light border-t-xs border-dashed w-full;
        }

        .dotted-line-text {
          @apply fonts-subtitle-small text-neutral-high-pure bg-neutral-low-dark rounded-lg;
          @apply absolute inline-flex h-6 p-micro justify-center items-center gap-1 flex-shrink-0 whitespace-nowrap;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);

          &[white=true] {
            @apply bg-neutral-high-light text-neutral-low-medium;
          }

          &.combo-conecte-only-tag-text {
            @apply bg-brand-secondary-dark text-neutral-high-pure max-w-full md:site:w-[406px] text-center;
          }
        }
      }
      .financing-value-container {
        @apply flex flex-col items-center gap-2;

        .financing-value-title {
          @apply fonts-body-medium-regular text-brand-primary-pure;
        }
        .financing-value-content {
          @apply flex;
          .currency-symbol {
            @apply text-brand-primary-pure;
            font-family: Lato;
            font-size: 14px;
            font-style: normal;
            font-weight: var(--Typography-Font-Weight-font-weight-bold, 700);
            line-height: 115%;
            letter-spacing: var(--Typography-Letter-Spacing-letter-spacing-default, 0px);
          }
          .approved-value {
            @apply  text-brand-primary-pure;
            font-family: Lato;
            font-size: 32px;
            font-style: normal;
            font-weight: 800;
            line-height: 115%;
            letter-spacing: var(--Typography-Letter-Spacing-letter-spacing-default, 0px);

          }
        }
      }
      .financing-info-container {
        @apply w-full flex flex-col items-center gap-6;
      }
      .simulation-values-container {
        @apply flex flex-col items-center gap-4 self-stretch;
        .simulation-values-content {
          @apply flex flex-col items-center gap-2 self-stretch;
          .simulation-values-list {
            @apply flex w-full justify-between items-center px-3  whitespace-nowrap;
            .simulation-values-item {
              @apply flex items-center gap-2;
              .simulation-values-item-icon {
                @apply w-6 h-6 text-neutral-low-light;
              }
              .simulation-values-item-title {
                @apply flex items-center justify-center fonts-body-medium-regular text-neutral-low-light;
              }
            }
            .simulation-values-alert-container {
              @apply flex items-center gap-1 flex-wrap;
              .simulation-values-alert-content {
                @apply flex items-center gap-[2px];
                .alert-title {
                  @apply flex items-center justify-center fonts-body-medium-regular;
                  &.-warning {
                    @apply text-feedback-warning-pure;
                  }
                  &.-positive {
                    @apply text-feedback-positive-pure;
                  }
                }
              }

              .simulation-values-alert-subtitle {
                @apply flex fonts-body-medium-regular text-neutral-low-light;
              }
            }
          }
          .simulation-values-dotted-line  {
            @apply border-t-neutral-high-light border-t-xs border-dashed w-full
          }
        }
      }
    }
  }

  .combo-conecte-consult-inverters-text {
    @apply flex flex-wrap justify-center items-center gap-x-1 fonts-body-small-regular text-neutral-low-medium mt-2 text-center;
  }

  .combo-conecte-consult-inverters-link {
    @apply text-neutral-low-medium underline cursor-pointer;
  }
}
</style>
